import { AdvertisersApi, type Advertiser } from '@/services/advertisers/advertisersApi'
import { PoliticalCampaignsApi } from '@/services/political/politicalCampaignsApi'
import type { PoliticalCampaign } from '@/types/models'

/**
 * The topics an advertiser may book a political line item against, keyed by
 * election id. A line item picks an election and then one of that election's
 * topics; the server validates the pair against the advertiser's political
 * campaign for that election (see `PoliticalLineItemValidationUtil` in core),
 * so anything outside this map would come back as a validation warning.
 */
export type TopicsByElection = Record<string, string[]>

/**
 * Groups the topics of political campaigns by the election each runs for. A
 * topic is identified by its name, falling back to its id — that is what the
 * line item's `politicalCampaignTopic` holds, and what core matches on.
 */
export function topicsByElectionFromCampaigns(campaigns: PoliticalCampaign[]): TopicsByElection {
  const byElection: TopicsByElection = {}
  for (const campaign of campaigns) {
    if (!campaign.electionId) continue
    const topics = campaign.topics.map((topic) => topic.name || topic.id).filter(Boolean)
    const existing = byElection[campaign.electionId] ?? []
    byElection[campaign.electionId] = [...new Set([...existing, ...topics])]
  }
  return byElection
}

/** The same map, read off an advertiser that declares its own political budgets. */
export function topicsByElectionFromBudgets(advertiser: Advertiser): TopicsByElection {
  const byElection: TopicsByElection = {}
  for (const budget of advertiser.politicalBudgets) {
    byElection[budget.electionId] = budget.topics
  }
  return byElection
}

export interface AdvertiserPoliticalTopics {
  topicsByElection: TopicsByElection
  /**
   * The only elections the advertiser can book — those of its linked political
   * campaigns, or else of its own political budgets — so the picker offers
   * only these. Unset when the advertiser has neither, or none could be read.
   */
  linkedElectionIds?: string[]
  /** The linked campaigns that could be read; empty for an advertiser booking against its budgets. */
  campaigns: PoliticalCampaign[]
}

/**
 * A linked campaign usually lives on the political network, out of reach of
 * the advertising network's context, so fall back to the anonymous public
 * endpoint — which only answers once the campaign's notice is public.
 */
async function readLinkedCampaign(id: string, publicId?: string): Promise<PoliticalCampaign | null> {
  const campaign = await PoliticalCampaignsApi.getById(id).catch(() => null)
  if (campaign || !publicId) return campaign
  return PoliticalCampaignsApi.getPublicById(publicId).catch(() => null)
}

/**
 * What the advertiser may book against. Campaign links win: an advertiser tied
 * to political campaigns takes its elections and topics from them, and only an
 * advertiser without any falls back to its own political budgets.
 */
export async function advertiserTopicsByElection(advertiserId: string): Promise<AdvertiserPoliticalTopics> {
  const advertiser = await AdvertisersApi.getById(advertiserId)
  if (!advertiser.politicalCampaignIds.length) {
    const topicsByElection = topicsByElectionFromBudgets(advertiser)
    const budgetElectionIds = Object.keys(topicsByElection)
    return {
      topicsByElection,
      linkedElectionIds: budgetElectionIds.length ? budgetElectionIds : undefined,
      campaigns: [],
    }
  }

  const publicIds = new Map(advertiser.politicalCampaigns.map((c) => [c.id, c.publicId]))
  const campaigns = (
    await Promise.all(advertiser.politicalCampaignIds.map((id) => readLinkedCampaign(id, publicIds.get(id))))
  ).filter((c): c is PoliticalCampaign => !!c)
  const linkedElectionIds = [...new Set(campaigns.map((c) => c.electionId).filter(Boolean))]
  return {
    topicsByElection: topicsByElectionFromCampaigns(campaigns),
    // A campaign the user cannot read contributes nothing; with none readable,
    // leave the elections open rather than offer an empty picker.
    linkedElectionIds: linkedElectionIds.length ? linkedElectionIds : undefined,
    campaigns,
  }
}

/** How many topics each election offers — what the election picker shows. */
export function topicCounts(topics: TopicsByElection): Record<string, number> {
  return Object.fromEntries(Object.entries(topics).map(([id, list]) => [id, list.length]))
}
